import moment from 'moment-timezone';
import type {List} from 'immutable';
import type {FieldType} from '@packages/systems/dynamo/records/Field';
import {isDateTimeField} from '@packages/systems/dynamo/utils/CollectionFieldUtils';

/**
 * Field types whose values can be rendered as secondary row text in the
 * curated item picker (and filtered on server-side via `fields[slug][op]=`
 * query params). Heavy or non-textual types (RichText, ImageRef, ItemRef,
 * Color, ...) are intentionally excluded from v1.
 */
const PICKER_FIELD_TYPES = [
  'Date',
  'Option',
  'PlainText',
  'Number',
  'Bool',
  'Link',
  'Email',
  'Phone',
];

/**
 * `name` is already the primary row text; the underscore-prefixed flags are
 * internal item state surfaced through the existing status prefix on labels.
 */
const EXCLUDED_FIELD_SLUGS = ['name', '_archived', '_draft', '_noSearch'];

const isPickerEligibleField = (field: FieldType): boolean =>
  PICKER_FIELD_TYPES.includes(field.get('type')) &&
  !EXCLUDED_FIELD_SLUGS.includes(field.get('slug'));

/**
 * Fields eligible for the "Show field" secondary-text selector.
 */
export const getDisplayableFields = (
  fields: List<FieldType>
): List<FieldType> => fields.filter(isPickerEligibleField);

/**
 * Fields eligible for the picker's filter row. Currently the same set as the
 * displayable fields — kept as a separate entry point so display and filter
 * eligibility can diverge without touching callers.
 */
export const getFilterableFields = (fields: List<FieldType>): List<FieldType> =>
  fields.filter(isPickerEligibleField);

export type CuratedPickerFilterOperator = {
  /** Query-param operator understood by the items endpoint (`fields[slug][<value>]=`). */
  value: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte';
  label: string;
};

const DATE_OPERATORS: Array<CuratedPickerFilterOperator> = [
  {value: 'gte', label: 'is on or after'},
  {value: 'lte', label: 'is on or before'},
  {value: 'gt', label: 'is after'},
  {value: 'lt', label: 'is before'},
  {value: 'eq', label: 'is'},
];

const EQUALITY_OPERATORS: Array<CuratedPickerFilterOperator> = [
  {value: 'eq', label: 'is'},
  {value: 'ne', label: 'is not'},
];

const NUMBER_OPERATORS: Array<CuratedPickerFilterOperator> = [
  {value: 'eq', label: 'equals'},
  {value: 'ne', label: 'does not equal'},
  {value: 'gt', label: 'is greater than'},
  {value: 'gte', label: 'is at least'},
  {value: 'lt', label: 'is less than'},
  {value: 'lte', label: 'is at most'},
];

const BOOL_OPERATORS: Array<CuratedPickerFilterOperator> = [
  {value: 'eq', label: 'is'},
];

/**
 * Operators the items endpoint supports for a given field type. Mirrors the
 * server's schemaValidator casts (see
 * entrypoints/server/lib/dynamo/schemaValidator/fields/).
 */
export const getFilterOperatorsForField = (
  field: FieldType
): Array<CuratedPickerFilterOperator> => {
  switch (field.get('type')) {
    case 'Date':
      return DATE_OPERATORS;
    case 'Number':
      return NUMBER_OPERATORS;
    case 'Bool':
      return BOOL_OPERATORS;
    default:
      return EQUALITY_OPERATORS;
  }
};

/**
 * Builds a `fields[slug][op]=value` query-string fragment for the items
 * endpoint, matching the format used by the CMS panel's filter popover and
 * consumed via the `fields` option of `useCollectionItemsPage`.
 */
export const buildFieldFilterQueryString = ({
  fieldSlug,
  operator,
  value,
}: {
  fieldSlug: string;
  operator: CuratedPickerFilterOperator['value'];
  value: string;
}): string => `fields[${fieldSlug}][${operator}]=${encodeURIComponent(value)}`;

export type CuratedPickerFilterCondition = {
  field: FieldType | null;
  operator: CuratedPickerFilterOperator['value'] | null;
  value: string;
};

/**
 * Expands one condition into operator/value pairs for the query.
 *
 * Date-time fields store timestamps but the picker's date input has day
 * granularity, so the day boundary depends on the operator: inclusive
 * operators pointing "past" the day (lte, gt) use end of day so items later
 * on the chosen day behave correctly, and eq becomes a start/end-of-day
 * range since no item matches midnight exactly.
 */
const buildConditionQueryPairs = (
  field: FieldType,
  operator: CuratedPickerFilterOperator['value'],
  value: string,
  timezone?: string
): Array<{
  operator: CuratedPickerFilterOperator['value'];
  value: string;
}> => {
  // isDateTimeField already returns false for non-Date fields.
  if (!isDateTimeField(field)) {
    return [{operator, value: buildFilterValueForQuery(field, value)}];
  }

  // Date-time values display in the site timezone, so day boundaries must be
  // computed there too — otherwise items late in the local day fall outside
  // a UTC-midnight boundary and the filter disagrees with what the user sees.
  const parsed = timezone
    ? moment.tz(value, 'YYYY-MM-DD', true, timezone)
    : moment.utc(value, 'YYYY-MM-DD', true);
  if (!parsed.isValid()) {
    return [{operator, value}];
  }

  const startOfDay = parsed.toISOString();
  const endOfDay = parsed.clone().endOf('day').toISOString();

  switch (operator) {
    case 'lte':
    case 'gt':
      return [{operator, value: endOfDay}];
    case 'eq':
      return [
        {operator: 'gte', value: startOfDay},
        {operator: 'lte', value: endOfDay},
      ];
    default:
      return [{operator, value: startOfDay}];
  }
};

/**
 * Builds the combined `fields[...]` query string for a list of filter
 * conditions. Complete conditions are ANDed by the server when their
 * fragments are joined with `&`. Conditions sharing a field and operator are
 * deduped (last wins) — repeated identical keys would be parsed into an
 * array server-side and fail the field cast.
 */
export const buildFieldFiltersQueryString = (
  conditions: Array<CuratedPickerFilterCondition>,
  {timezone}: {timezone?: string} = {}
): string => {
  const fragmentsByKey = new Map<string, string>();

  for (const {field, operator, value} of conditions) {
    if (!field || !operator || value === '') {
      continue;
    }
    const fieldSlug = field.get('slug');
    for (const pair of buildConditionQueryPairs(
      field,
      operator,
      value,
      timezone
    )) {
      fragmentsByKey.set(
        `${fieldSlug}\u0000${pair.operator}`,
        buildFieldFilterQueryString({
          fieldSlug,
          operator: pair.operator,
          value: pair.value,
        })
      );
    }
  }

  return [...fragmentsByKey.values()].join('&');
};

/**
 * Normalizes a raw filter input value for the items-endpoint query. Date
 * inputs produce `YYYY-MM-DD`, which is converted to a UTC ISO string to
 * match how CMS Date field values are stored; other types pass through.
 */
export const buildFilterValueForQuery = (
  field: FieldType,
  rawValue: string
): string => {
  if (field.get('type') === 'Date') {
    const parsed = moment.utc(rawValue, 'YYYY-MM-DD', true);
    if (parsed.isValid()) {
      return parsed.toISOString();
    }
  }
  return rawValue;
};
